import { useBookingModuleStoreActions, useCalendarRoot, useCalendarRootActions } from "PFApp/booking/store";
import { useCallback, useRef } from "react";

import { BarDataType, BOOKING_DRAGGING_PLACEHOLDER_ATTRIBUTE } from "../../bars/bars_data_attributes";

type CachedBookingBarElement = {
  element: HTMLElement;
  bookingId: number;
  rowKey: string;
};

const getBookingBarSelector = (bookingId: number): string =>
  [
    `[data-type="${BarDataType.Booking}"]`,
    `[data-entity-id="${bookingId}"]`,
    `:not([${BOOKING_DRAGGING_PLACEHOLDER_ATTRIBUTE}])`
  ].join("");

export const findBookingBarElement = (
  rowElement: HTMLElement | null,
  bookingId: number
): HTMLElement | null => rowElement?.querySelector<HTMLElement>(getBookingBarSelector(bookingId)) ?? null;

export const useBookingBarElement = () => {
  const calendarRoot = useCalendarRoot();
  const { getRowsWithRefs } = useCalendarRootActions(calendarRoot);
  const { getBookingDragAndDropData } = useBookingModuleStoreActions();
  const cacheRef = useRef<CachedBookingBarElement | null>(null);

  const getBookingBarElement = useCallback((): HTMLElement | null => {
    const { bookingId, sourceRow } = getBookingDragAndDropData();
    if (!bookingId || !sourceRow) {
      return null;
    }

    const cached = cacheRef.current;
    if (cached?.element.isConnected && cached.bookingId === bookingId && cached.rowKey === sourceRow.key) {
      return cached.element;
    }

    const rowElement = getRowsWithRefs().get(sourceRow.key)?.rowRef ?? null;
    const element = findBookingBarElement(rowElement, bookingId);
    cacheRef.current = element ? { element, bookingId, rowKey: sourceRow.key } : null;

    return element;
  }, [getBookingDragAndDropData, getRowsWithRefs]);

  return { getBookingBarElement };
};
